import { useEffect, useState } from 'react';
import { useAuth } from '../features/auth/AuthContext';
import { useAnnouncer } from '../components/LiveAnnouncer';
import { usePageTitle } from '../lib/usePageTitle';
import { AppLayout } from '../components/AppLayout';
import { TextField } from '../components/TextField';
import { Button } from '../components/Button';
import { api, ApiError } from '../lib/api';
import type { PublicUser } from '../features/auth/types';

/**
 * Il mio profilo: dati, cambio password, preferenze di accessibilità.
 * Ogni salvataggio è annunciato; gli errori sono role=alert.
 */
export function ProfilePage() {
  usePageTitle('Il mio profilo');
  const { user } = useAuth();

  return (
    <AppLayout title="Il mio profilo Panda">
      <p className="mt-2 text-ink-700 dark:text-ink-200">{user?.email}</p>
      <ProfileSection />
      <A11ySection />
      <NotificationPrefsSection />
      <PasswordSection />
      <DataSection />
    </AppLayout>
  );
}

const NOTIFICATION_LABELS: Record<string, string> = {
  MATCHDAY_OPEN: 'Giornata aperta',
  MATCHDAY_REMINDER: 'Promemoria pronostici',
  RESULTS_AVAILABLE: 'Risultati disponibili',
  DISPUTE_UPDATED: 'Contestazioni',
  INVITE_RECEIVED: 'Inviti ricevuti',
};

/** Preferenze notifiche per tipo e canale (lineeguida §44). */
function NotificationPrefsSection() {
  const { announce } = useAnnouncer();
  const [prefs, setPrefs] = useState<Array<{ type: string; inApp: boolean; email: boolean }> | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api<{ preferences: Array<{ type: string; inApp: boolean; email: boolean }> }>(
      '/users/me/notification-preferences',
    )
      .then((r) => setPrefs(r.preferences))
      .catch(() => setPrefs([]));
  }, []);

  if (!prefs || prefs.length === 0) return null;

  const toggle = (type: string, channel: 'inApp' | 'email') => {
    setPrefs(prefs.map((p) => (p.type === type ? { ...p, [channel]: !p[channel] } : p)));
  };

  return (
    <section aria-labelledby="sez-notifiche" className="mt-10 max-w-xl">
      <h2 id="sez-notifiche" className="text-2xl font-bold tracking-tight">Notifiche</h2>
      <div className="mt-4 overflow-x-auto">
        <table className="w-full border-collapse text-left">
          <caption className="visually-hidden">
            Preferenze notifiche: per ogni tipo scegli i canali attivi
          </caption>
          <thead>
            <tr className="border-b-2 border-ink-300 dark:border-ink-600">
              <th scope="col" className="py-2 pr-3">Evento</th>
              <th scope="col" className="py-2 pr-3">Nell’app</th>
              <th scope="col" className="py-2">Email</th>
            </tr>
          </thead>
          <tbody>
            {prefs.map((p) => (
              <tr key={p.type} className="border-b border-ink-200 dark:border-ink-800">
                <th scope="row" className="py-2.5 pr-3 font-medium">
                  {NOTIFICATION_LABELS[p.type] ?? p.type}
                </th>
                {(['inApp', 'email'] as const).map((channel) => (
                  <td key={channel} className="py-2.5 pr-3">
                    <input
                      type="checkbox"
                      aria-label={`${channel === 'inApp' ? 'Notifica nell’app' : 'Email'} per ${NOTIFICATION_LABELS[p.type] ?? p.type}`}
                      className="h-5 w-5 accent-panda-700"
                      checked={p[channel]}
                      onChange={() => toggle(p.type, channel)}
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="mt-3">
        <Button
          variant="secondary"
          loading={saving}
          loadingText="Salvataggio…"
          onClick={async () => {
            setSaving(true);
            try {
              await api('/users/me/notification-preferences', {
                method: 'PUT',
                body: { preferences: prefs },
              });
              announce('Preferenze notifiche salvate.');
            } catch {
              announce('Salvataggio non riuscito. Riprova.', 'assertive');
            } finally {
              setSaving(false);
            }
          }}
        >
          Salva preferenze notifiche
        </Button>
      </div>
    </section>
  );
}

/** I miei dati (GDPR): esportazione e cancellazione account. */
function DataSection() {
  const { announce } = useAnnouncer();
  const { logout } = useAuth();
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  return (
    <section aria-labelledby="sez-dati" className="mt-10 max-w-xl">
      <h2 id="sez-dati" className="text-2xl font-bold tracking-tight">I miei dati</h2>

      <div className="mt-4">
        <a
          href="/api/users/me/export"
          download
          className="button inline-flex items-center justify-center rounded-xl border-2 border-panda-700 px-5 py-2.5 font-semibold text-panda-700 no-underline hover:bg-panda-50 dark:border-panda-400 dark:text-panda-300"
        >
          Scarica tutti i miei dati (JSON)
        </a>
        <p className="mt-2 text-sm text-ink-600 dark:text-ink-300">
          Account, leghe, pronostici, punteggi, contestazioni e notifiche in un unico file.
        </p>
      </div>

      <div className="mt-8 rounded-2xl border-2 border-danger p-5">
        <h3 className="text-lg font-bold text-danger">Cancella il mio account</h3>
        <p className="mt-1 text-sm text-ink-700 dark:text-ink-200">
          Operazione irreversibile: i tuoi dati personali vengono rimossi. I punteggi delle
          giornate già giocate restano in forma anonima, per non falsare le classifiche degli
          altri partecipanti.
        </p>
        {error && (
          <p role="alert" className="mt-3 font-medium text-danger">{error}</p>
        )}
        {!confirmingDelete ? (
          <div className="mt-3">
            <Button variant="danger" onClick={() => setConfirmingDelete(true)}>
              Voglio cancellare il mio account
            </Button>
          </div>
        ) : (
          <form
            noValidate
            className="mt-3 flex flex-col gap-3"
            onSubmit={async (e) => {
              e.preventDefault();
              setError(null);
              if (!deletePassword) {
                setError('Inserisci la password per confermare.');
                return;
              }
              setDeleting(true);
              try {
                await api('/users/me/delete', { method: 'POST', body: { password: deletePassword } });
                announce('Account cancellato. Arrivederci.', 'assertive');
                await logout();
                window.location.href = '/';
              } catch (err) {
                const msg = err instanceof ApiError ? err.message : 'Cancellazione non riuscita.';
                setError(msg);
                announce(`Errore: ${msg}`, 'assertive');
                setDeleting(false);
              }
            }}
          >
            <TextField
              label="Conferma con la tua password"
              type="password"
          revealable
              autoComplete="current-password"
              required
              value={deletePassword}
              onChange={(e) => setDeletePassword(e.target.value)}
            />
            <div className="flex gap-3">
              <Button type="submit" variant="danger" loading={deleting} loadingText="Cancellazione…">
                Cancella definitivamente
              </Button>
              <Button variant="secondary" onClick={() => setConfirmingDelete(false)}>
                Annulla
              </Button>
            </div>
          </form>
        )}
      </div>
    </section>
  );
}

function ProfileSection() {
  const { user, setUser } = useAuth();
  const { announce } = useAnnouncer();
  const [displayName, setDisplayName] = useState(user?.displayName ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  return (
    <section aria-labelledby="sez-profilo" className="mt-8 max-w-md">
      <h2 id="sez-profilo" className="text-2xl font-bold tracking-tight">Dati profilo</h2>
      {error && <p role="alert" className="mt-3 font-medium text-danger">{error}</p>}
      <form
        noValidate
        className="mt-4 flex flex-col gap-4"
        onSubmit={async (e) => {
          e.preventDefault();
          setError(null);
          if (displayName.trim().length < 2) {
            setError('Il nome deve avere almeno 2 caratteri.');
            return;
          }
          setSaving(true);
          try {
            const res = await api<{ user: PublicUser }>('/users/me', {
              method: 'PATCH',
              body: { displayName: displayName.trim() },
            });
            setUser(res.user);
            announce('Nome aggiornato.');
          } catch (err) {
            setError(err instanceof ApiError ? err.message : 'Errore nel salvataggio.');
          } finally {
            setSaving(false);
          }
        }}
      >
        <TextField
          label="Nome visualizzato"
          description="Il nome che appare in classifica e nelle leghe."
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          required
        />
        <Button type="submit" loading={saving} loadingText="Salvataggio…">
          Salva il nome
        </Button>
      </form>
    </section>
  );
}

const A11Y_OPTIONS: Array<{ key: string; label: string; description: string }> = [
  {
    key: 'highContrast',
    label: 'Alto contrasto',
    description: 'Colori più netti e testo più scuro per una migliore leggibilità.',
  },
  {
    key: 'largeText',
    label: 'Testo grande',
    description: 'Aumenta la dimensione del testo di tutta l’applicazione del 25%.',
  },
  {
    key: 'reduceMotion',
    label: 'Riduci le animazioni',
    description: 'Elimina transizioni e movimenti. Attiva automaticamente se il tuo sistema lo richiede.',
  },
  {
    key: 'simplifiedUi',
    label: 'Interfaccia semplificata',
    description: 'Layout più lineare con meno elementi decorativi.',
  },
  {
    key: 'screenReaderMode',
    label: 'Modalità screen reader',
    description: 'Annunci più descrittivi e tabelle al posto di ogni contenuto solo visivo.',
  },
];

function A11ySection() {
  const { user, setUser } = useAuth();
  const { announce } = useAnnouncer();
  const prefs = (user?.a11yPreferences ?? {}) as Record<string, boolean>;

  const toggle = async (key: string, label: string) => {
    const next = { ...prefs, [key]: !prefs[key] };
    try {
      const res = await api<{ user: PublicUser }>('/users/me/a11y', { method: 'PATCH', body: next });
      setUser(res.user);
      announce(`${label}: ${next[key] ? 'attivato' : 'disattivato'}.`);
    } catch {
      announce(`Non sono riuscito a salvare la preferenza ${label}. Riprova.`, 'assertive');
    }
  };

  return (
    <section aria-labelledby="sez-a11y" className="mt-10 max-w-xl">
      <h2 id="sez-a11y" className="text-2xl font-bold tracking-tight">Accessibilità</h2>
      <p className="mt-1 text-ink-700 dark:text-ink-200">
        Le preferenze si applicano subito e vengono ricordate su tutti i tuoi dispositivi.
      </p>
      <ul className="mt-4 flex flex-col gap-1">
        {A11Y_OPTIONS.map((option) => (
          <li key={option.key}>
            <label className="flex min-h-11 cursor-pointer items-start gap-3 rounded-xl p-3 hover:bg-ink-50 dark:hover:bg-ink-900">
              <input
                type="checkbox"
                className="mt-1 h-5 w-5 accent-panda-700"
                checked={!!prefs[option.key]}
                onChange={() => toggle(option.key, option.label)}
              />
              <span>
                <span className="font-semibold">{option.label}</span>
                <span className="block text-sm text-ink-600 dark:text-ink-300">
                  {option.description}
                </span>
              </span>
            </label>
          </li>
        ))}
      </ul>
    </section>
  );
}

function PasswordSection() {
  const { announce } = useAnnouncer();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  return (
    <section aria-labelledby="sez-password" className="mt-10 max-w-md">
      <h2 id="sez-password" className="text-2xl font-bold tracking-tight">Cambia password</h2>
      {error && <p role="alert" className="mt-3 font-medium text-danger">{error}</p>}
      {done && (
        <p role="status" className="mt-3 font-medium text-success">
          Password aggiornata. Le altre sessioni sono state disconnesse.
        </p>
      )}
      <form
        noValidate
        className="mt-4 flex flex-col gap-4"
        onSubmit={async (e) => {
          e.preventDefault();
          setError(null);
          setDone(false);
          if (next !== confirm) {
            setError('Le due password nuove non coincidono.');
            announce('Errore: le due password nuove non coincidono.', 'assertive');
            return;
          }
          setSaving(true);
          try {
            await api('/users/me/change-password', {
              method: 'POST',
              body: { currentPassword: current, newPassword: next },
            });
            setDone(true);
            setCurrent('');
            setNext('');
            setConfirm('');
            announce('Password aggiornata.');
          } catch (err) {
            const message = err instanceof ApiError ? err.message : 'Errore nel cambio password.';
            setError(message);
            announce(`Errore: ${message}`, 'assertive');
          } finally {
            setSaving(false);
          }
        }}
      >
        <TextField
          label="Password attuale"
          type="password"
          revealable
          autoComplete="current-password"
          required
          value={current}
          onChange={(e) => setCurrent(e.target.value)}
        />
        <TextField
          label="Nuova password"
          type="password"
          revealable
          description="Almeno 10 caratteri, con almeno una lettera e un numero."
          autoComplete="new-password"
          required
          value={next}
          onChange={(e) => setNext(e.target.value)}
        />
        <TextField
          label="Conferma nuova password"
          type="password"
          revealable
          autoComplete="new-password"
          required
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
        />
        <Button type="submit" loading={saving} loadingText="Aggiornamento…">
          Aggiorna password
        </Button>
      </form>
    </section>
  );
}
